import { DEFAULT_STORE_OBJECT } from '@/redux/constants';
import type { DefaultStoreObject } from '@/redux/types';
import { LOSS_AND_DAMAGE_FILTER_TYPES } from '@/views/screens/Claims/constants';
import type {
  LossAndDamageClaim,
  LossAndDamageClaimComment,
  LossAndDamageClaimDetailed,
  LossAndDamageClaimFile,
  LossAndDamageClaimItem,
  LossAndDamageClaimSettlement,
  LossAndDamageFilterType,
  LossAndDamageSearchFilters,
} from '@/views/screens/Claims/types';
import { cloneDeep } from 'es-toolkit';
import { LOSS_AND_DAMAGE_CLAIMS_FILTERS_INITIAL_DATA } from '../../../contants/ClaimsEnums';
import { SEARCH_VIEW_TYPES, type SearchViewType } from '../../../contants/common';
import LossAndDamageClaimsTypes from './LossAndDamageClaimsTypes';

export interface LossAndDamageClaimsState {
  filters: LossAndDamageSearchFilters;
  filterType: LossAndDamageFilterType;
  editDrawer: boolean;
  claimsList: DefaultStoreObject<LossAndDamageClaim[]> & {
    footerData: {
      totalClaim: number;
      openClaim: number;
      closedClaim: number;
      holdClaim: number;
      filedAmount: number;
      paidAmount: number;
      preClaim?: number;
    };
    isFirstTime: boolean;
  };
  selectedClaims: LossAndDamageClaim['claimKey'][];
  claimDetails: DefaultStoreObject<LossAndDamageClaimDetailed>;
  createClaim: DefaultStoreObject<null>;
  checkXpoExport: DefaultStoreObject<string>;
  claimViewMode: SearchViewType;
  tableViewColumns: DefaultStoreObject<
    {
      columnName: string;
      columnValue: string;
    }[]
  >;
  sortingFields: DefaultStoreObject<
    {
      key: string;
      value: string;
    }[]
  >;
  saveTableViewColumns: DefaultStoreObject<
    {
      columnName: string;
      columnValue: string;
      id: string;
    }[]
  >;
  claimNotes: DefaultStoreObject<LossAndDamageClaimComment[]> & {
    totalRows?: number;
  };
  claimSettlements: DefaultStoreObject<LossAndDamageClaimSettlement[]> & {
    totalRows?: number;
    totalAmount?: number;
  };
  claimItems: DefaultStoreObject<LossAndDamageClaimItem[]> & {
    totalRows?: number;
  };
  claimEmailStatus: DefaultStoreObject<unknown>;
  claimFiles: DefaultStoreObject<LossAndDamageClaimFile[]> & {
    totalRows?: number;
    totalAmount?: number;
  };
}

const initialState: LossAndDamageClaimsState = {
  filters: LOSS_AND_DAMAGE_CLAIMS_FILTERS_INITIAL_DATA,
  filterType: LOSS_AND_DAMAGE_FILTER_TYPES.BASIC,
  editDrawer: false,
  claimsList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    footerData: {
      totalClaim: 0,
      openClaim: 0,
      closedClaim: 0,
      holdClaim: 0,
      filedAmount: 0,
      paidAmount: 0,
    },
    isFirstTime: true,
  },
  selectedClaims: [],
  claimDetails: {
    ...DEFAULT_STORE_OBJECT,
    data: {} as LossAndDamageClaimDetailed,
  },
  createClaim: {
    ...DEFAULT_STORE_OBJECT,
  },
  checkXpoExport: {
    ...DEFAULT_STORE_OBJECT,
  },
  claimViewMode: SEARCH_VIEW_TYPES.CARD,
  tableViewColumns: { ...DEFAULT_STORE_OBJECT, data: [] },
  sortingFields: { ...DEFAULT_STORE_OBJECT, data: [] },
  saveTableViewColumns: { ...DEFAULT_STORE_OBJECT, data: [] },
  claimNotes: { ...DEFAULT_STORE_OBJECT, data: [], totalRows: 0 },
  claimSettlements: { ...DEFAULT_STORE_OBJECT, data: [], totalRows: 0, totalAmount: 0 },
  claimItems: { ...DEFAULT_STORE_OBJECT, data: [], totalRows: 0 },
  claimEmailStatus: { ...DEFAULT_STORE_OBJECT, data: [] },
  claimFiles: { ...DEFAULT_STORE_OBJECT, data: [], totalRows: 0 },
};

const LossAndDamageClaimsReducer = (
  // biome-ignore lint/style/useDefaultParameterLast: <explanation>
  state = initialState,
  { type, payload }: any,
): LossAndDamageClaimsState => {
  switch (type) {
    case LossAndDamageClaimsTypes.SET_CLAIMS_FILTERS: {
      return {
        ...state,
        filters: { ...state.filters, ...payload },
      };
    }
    case LossAndDamageClaimsTypes.SET_FILTER_TYPE: {
      return {
        ...state,
        filterType: payload,
      };
    }
    case LossAndDamageClaimsTypes.SET_SELECTED_CLAIMS: {
      return {
        ...state,
        selectedClaims: payload,
      };
    }
    case LossAndDamageClaimsTypes.SET_CLAIM_EDIT_DRAWER: {
      return {
        ...state,
        editDrawer: payload,
      };
    }

    case LossAndDamageClaimsTypes.GET_CLAIMS_LIST_PROGRESS: {
      return {
        ...state,
        claimsList: {
          ...state.claimsList,
          ...DEFAULT_STORE_OBJECT,
          data: state.filters.pageNo === 1 ? [] : state.claimsList.data,
          progressing: true,
          isFirstTime: false,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIMS_LIST_SUCCESS: {
      let selectedClaims = cloneDeep(state.selectedClaims);
      if (
        state.selectedClaims?.length === state?.claimsList?.data?.length &&
        state.selectedClaims?.length !== 0 // To fixe select all checkbox issue
      ) {
        // @ts-ignore
        payload.data.forEach(({ claimKey }) => selectedClaims.push(claimKey));
      } else if (state.filters.pageNo === 1) {
        selectedClaims = []; // To solve claim headers issue
      }
      return {
        ...state,
        selectedClaims,
        claimsList: {
          ...state.claimsList,
          ...DEFAULT_STORE_OBJECT,
          data: [...(state.claimsList.data || []), ...payload.data],
          footerData: payload.footerData,
          success: true,
          isFirstTime: false,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIMS_LIST_ERROR: {
      return {
        ...state,
        claimsList: {
          ...state.claimsList,
          ...DEFAULT_STORE_OBJECT,
          ...payload,
          data: state.claimsList.data,
          error: true,
          isFirstTime: false,
        },
      };
    }
    case LossAndDamageClaimsTypes.SET_CLAIMS_LIST: {
      return {
        ...state,
        claimsList: {
          ...state.claimsList,
          ...DEFAULT_STORE_OBJECT,
          ...payload,
          error: true,
        },
      };
    }

    case LossAndDamageClaimsTypes.GET_CLAIM_DETAILS_PROGRESS: {
      return {
        ...state,
        claimDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: null,
        },
        claimNotes: { ...state.claimNotes, data: [], totalRows: 0 },
        claimFiles: { ...state.claimFiles, data: [], totalRows: 0 },
        claimItems: { ...state.claimItems, data: [], totalRows: 0 },
        claimSettlements: { ...state.claimSettlements, data: [], totalRows: 0 },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_DETAILS_SUCCESS: {
      return {
        ...state,
        claimDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_DETAILS_ERROR: {
      return {
        ...state,
        claimDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case LossAndDamageClaimsTypes.CREATE_CLAIM_PROGRESS: {
      return {
        ...state,
        createClaim: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case LossAndDamageClaimsTypes.CREATE_CLAIM_SUCCESS: {
      return {
        ...state,
        createClaim: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }
    case LossAndDamageClaimsTypes.CREATE_CLAIM_ERROR: {
      return {
        ...state,
        createClaim: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
        },
      };
    }
    case LossAndDamageClaimsTypes.CREATE_CLAIM_RESET: {
      return {
        ...state,
        createClaim: {
          ...DEFAULT_STORE_OBJECT,
        },
      };
    }

    case LossAndDamageClaimsTypes.CHECK_EXP_EXPORT_PROGRESS: {
      return {
        ...state,
        checkXpoExport: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case LossAndDamageClaimsTypes.CHECK_EXP_EXPORT_SUCCESS: {
      return {
        ...state,
        checkXpoExport: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case LossAndDamageClaimsTypes.CHECK_EXP_EXPORT_ERROR: {
      return {
        ...state,
        checkXpoExport: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
        },
      };
    }

    case LossAndDamageClaimsTypes.SET_CLAIM_SEARCH_VIEW_TYPE_INITIAL:
    case LossAndDamageClaimsTypes.SET_CLAIM_SEARCH_VIEW_TYPE: {
      return {
        ...state,
        claimViewMode: payload,
      };
    }

    case LossAndDamageClaimsTypes.GET_TABLE_VIEW_COLUMNS_PROGRESS: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_TABLE_VIEW_COLUMNS_SUCCESS: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_TABLE_VIEW_COLUMNS_ERROR: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case LossAndDamageClaimsTypes.GET_SORTING_FIELDS_PROGRESS: {
      return {
        ...state,
        sortingFields: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_SORTING_FIELDS_SUCCESS: {
      return {
        ...state,
        sortingFields: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_SORTING_FIELDS_ERROR: {
      return {
        ...state,
        sortingFields: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case LossAndDamageClaimsTypes.SAVE_TABLE_VIEW_COLUMNS_PROGRESS: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case LossAndDamageClaimsTypes.SAVE_TABLE_VIEW_COLUMNS_SUCCESS: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case LossAndDamageClaimsTypes.SAVE_TABLE_VIEW_COLUMNS_ERROR: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case LossAndDamageClaimsTypes.GET_CLAIM_NOTES_PROGRESS: {
      return {
        ...state,
        claimNotes: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload.pageIndex === 1 ? [] : state.claimNotes.data,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_NOTES_SUCCESS: {
      return {
        ...state,
        claimNotes: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: !payload.removeOld ? state.claimNotes.data?.concat(payload.data) : payload.data,
          totalRows: payload.totalRows,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_NOTES_ERROR: {
      return {
        ...state,
        claimNotes: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: state.claimNotes.data,
          totalRows: state.claimNotes.totalRows,
          ...payload,
        },
      };
    }

    case LossAndDamageClaimsTypes.GET_CLAIM_SETTLEMENTS_PROGRESS: {
      return {
        ...state,
        claimSettlements: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload.pageIndex === 1 ? [] : state.claimSettlements.data,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_SETTLEMENTS_SUCCESS: {
      return {
        ...state,
        claimSettlements: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: !payload.removeOld ? state.claimSettlements.data?.concat(payload.data) : payload.data,
          totalRows: payload.totalRows,
          totalAmount: payload.totalAmount,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_SETTLEMENTS_ERROR: {
      return {
        ...state,
        claimSettlements: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: state.claimSettlements.data,
          totalRows: state.claimSettlements.totalRows,
          ...payload,
        },
      };
    }

    case LossAndDamageClaimsTypes.GET_CLAIM_ITEMS_PROGRESS: {
      return {
        ...state,
        claimItems: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload.pageIndex === 1 ? [] : state.claimItems.data,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_ITEMS_SUCCESS: {
      return {
        ...state,
        claimItems: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: !payload.removeOld ? state.claimItems.data?.concat(payload.data) : payload.data,
          totalRows: payload.totalRows,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_ITEMS_ERROR: {
      return {
        ...state,
        claimItems: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: state.claimItems.data,
          totalRows: state.claimItems.totalRows,
          ...payload,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_EMAIL_STATUS_PROGRESS: {
      return {
        ...state,
        claimEmailStatus: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_EMAIL_STATUS_SUCCESS: {
      return {
        ...state,
        claimEmailStatus: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_EMAIL_STATUS_ERROR: {
      return {
        ...state,
        claimEmailStatus: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case LossAndDamageClaimsTypes.GET_CLAIM_FILES_PROGRESS: {
      return {
        ...state,
        claimFiles: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload.pageIndex === 1 ? [] : state.claimFiles.data,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_FILES_SUCCESS: {
      return {
        ...state,
        claimFiles: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: !payload.removeOld ? state.claimFiles.data?.concat(payload.data) : payload.data,
          totalRows: payload.totalRows,
        },
      };
    }
    case LossAndDamageClaimsTypes.GET_CLAIM_FILES_ERROR: {
      return {
        ...state,
        claimFiles: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: state.claimFiles.data,
          totalRows: state.claimFiles.totalRows,
        },
      };
    }

    default:
      return state;
  }
};

export default LossAndDamageClaimsReducer;
